/* ──────────────────────────────────────────────────────────────
 *  useJobStore — Background export job management
 *
 *  KEYING FIX: jobs and geometry-readiness are now keyed by the
 *  composite (jobId, sampleId) pair, not jobId alone. One compute
 *  run's job_id is reused across many sample selections, so keying
 *  by job_id alone caused each sample switch to silently overwrite
 *  the previous sample's job record — both here and in the backend
 *  JobManager.
 * ────────────────────────────────────────────────────────────── */

import { create } from 'zustand';

import type {
  ExportFileType,
  FileStatusInfo,
  JobStatus,
  JobStatusResponse,
} from '@/types/job';

export function jobKey(jobId: string, sampleId: string): string {
  return `${jobId}::${sampleId}`;
}

/** A single export job record, scoped to one (jobId, sampleId) pair. */
export interface JobRecord {
  jobId: string;
  sessionId: string;
  status: JobStatus;
  bushingType: string;
  sampleId: string;
  files: Record<string, FileStatusInfo>;
  createdAt: string;
  completedAt: string | null;
}

interface JobState {
  /** Job ID created during the current compute session. Null until
   *  a new compute run's geometry has succeeded for its best sample —
   *  see useComputeExecution. Cleared to null the instant a NEW
   *  compute starts, so no stale job_id can be reused against a
   *  different compute run's samples. */
  computeJobId: string | null;

  /** Raw backend job_id of the job currently shown/polled in the UI. */
  currentJobId: string | null;

  /** Sample this currentJobId's record belongs to. Required because
   *  job records are now keyed by (jobId, sampleId), not jobId alone. */
  currentSampleId: string | null;

  /** All export jobs created during the current session, keyed by
   *  jobKey(jobId, sampleId). */
  jobs: Record<string, JobRecord>;

  /** Whether polling is active for the current job. */
  isPolling: boolean;

  /** Confirms a (jobId, sampleId) pair has backend job_state ready —
   *  i.e. at least one /mesh call succeeded for that exact pair.
   *  Keyed by jobKey(jobId, sampleId). See point #4 caveat below. */
  geometryReady: Record<string, true>;

  /* ── Actions ──────────────────────────────────────────── */

  setComputeJobId: (jobId: string | null) => void;

  createJob: (
    jobId: string,
    sessionId: string,
    bushingType: string,
    sampleId: string,
    makeCurrent?: boolean,
  ) => void;

  setCurrentJob: (jobId: string | null, sampleId?: string | null) => void;

  updateJobFromResponse: (response: JobStatusResponse) => void;

  setPolling: (active: boolean) => void;

  getFileStatus: (fileType: ExportFileType) => FileStatusInfo | null;

  getDownloadUrl: (fileType: ExportFileType) => string | null;

  updateFileDownloadUrl: (
    jobId: string,
    sampleId: string,
    fileType: ExportFileType,
    downloadUrl: string,
  ) => void;

  markGeometryReady: (jobId: string, sampleId: string) => void;

  isGeometryReady: (jobId: string, sampleId: string) => boolean;

  resetJobs: () => void;
}

export const useJobStore = create<JobState>()((set, get) => ({
  computeJobId: null,
  currentJobId: null,
  currentSampleId: null,
  jobs: {},
  isPolling: false,
  geometryReady: {},

  setComputeJobId: (jobId) =>
    set({
      computeJobId: jobId,
    }),

  createJob: (jobId, sessionId, bushingType, sampleId, makeCurrent = true) =>
    set((state) => {
      const key = jobKey(jobId, sampleId);

      return {
        currentJobId: makeCurrent ? jobId : state.currentJobId,
        currentSampleId: makeCurrent ? sampleId : state.currentSampleId,

        jobs: {
          ...state.jobs,
          [key]: {
            jobId,
            sessionId,
            status: 'running',
            bushingType,
            sampleId,
            files: {},
            createdAt: new Date().toISOString(),
            completedAt: null,
          },
        },
      };
    }),

  setCurrentJob: (jobId, sampleId = null) =>
    set((state) => {
      if (jobId === null || sampleId === null) {
        return {
          currentJobId: null,
          currentSampleId: null,
          isPolling: false,
        };
      }

      const key = jobKey(jobId, sampleId);

      if (!state.jobs[key]) {
        return state;
      }

      return {
        currentJobId: jobId,
        currentSampleId: sampleId,
      };
    }),

  updateJobFromResponse: (response) =>
    set((state) => {
      // response.sample_id must be present — backend get_job_status
      // now returns it per-(job_id, sample_id) record. If your
      // JobStatusResponse type doesn't have it yet, the backend
      // jobs router needs updating (see note below).
      const key = jobKey(response.job_id, response.sample_id);
      const existingJob = state.jobs[key];

      if (!existingJob) {
        return state;
      }

      return {
        jobs: {
          ...state.jobs,
          [key]: {
            ...existingJob,
            status: response.status,
            files: response.files,
            completedAt: response.completed_at,
          },
        },
      };
    }),

  setPolling: (active) =>
    set({
      isPolling: active,
    }),

  getFileStatus: (fileType) => {
    const { currentJobId, currentSampleId, jobs } = get();

    if (!currentJobId || !currentSampleId) {
      return null;
    }

    const currentJob = jobs[jobKey(currentJobId, currentSampleId)];

    if (!currentJob) {
      return null;
    }

    return currentJob.files[fileType] ?? null;
  },

  getDownloadUrl: (fileType) => {
    const fileInfo = get().getFileStatus(fileType);
    return fileInfo?.download_url ?? null;
  },

  updateFileDownloadUrl: (jobId, sampleId, fileType, downloadUrl) =>
    set((state) => {
      const key = jobKey(jobId, sampleId);
      const job = state.jobs[key];

      if (!job) {
        return state;
      }

      const fileInfo = job.files[fileType];

      if (!fileInfo) {
        return state;
      }

      return {
        jobs: {
          ...state.jobs,
          [key]: {
            ...job,
            files: {
              ...job.files,
              [fileType]: {
                ...fileInfo,
                download_url: downloadUrl,
              },
            },
          },
        },
      };
    }),

  markGeometryReady: (jobId, sampleId) =>
    set((state) => ({
      geometryReady: {
        ...state.geometryReady,
        [jobKey(jobId, sampleId)]: true,
      },
    })),

  isGeometryReady: (jobId, sampleId) =>
    Boolean(get().geometryReady[jobKey(jobId, sampleId)]),

  resetJobs: () =>
    set({
      computeJobId: null,
      currentJobId: null,
      currentSampleId: null,
      jobs: {},
      isPolling: false,
      geometryReady: {},
    }),
}));